import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from 'reactstrap';
import CustomSelectDropdown from 'components/shared/DropDown/CustomSelectDropdown';
import GapGenerationModal from 'components/shared/modal/GapGenerationModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import axiosPrivate from 'config/axiosPrivate';
import './SmartContentPopin.css';

const DEFAULT_LLM = 'claude_gpt';
const DEFAULT_ENRICHMENT_LLM = 'claude';
const MAX_KNOWLEDGE_BASE_FILES = 3;
const KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
const KNOWLEDGE_BASE_ACCEPT =
  '.pdf,.docx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv';

const getFileExtension = (fileName = '') => {
  const match = String(fileName).toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

const isAcceptedKnowledgeBaseFile = (file) =>
  KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS.includes(getFileExtension(file?.name));

const normalizeExistingKnowledgeBaseFiles = (files) => {
  if (!Array.isArray(files)) return [];
  return files
    .map((file) => ({
      id: String(file?.id || file?.fileId || '').trim(),
      originalName: String(
        file?.originalName || file?.name || file?.fileName || ''
      ).trim(),
      extension: String(file?.extension || '').trim(),
      size: file?.size,
      uploadedAt: file?.uploadedAt,
    }))
    .filter((file) => file.id || file.originalName);
};

const getExistingKnowledgeBaseFilesKey = (files) => {
  if (!Array.isArray(files)) return null;
  return files
    .map((file) => String(file?.id || file?.fileId || file?.originalName || file?.name || '').trim())
    .filter(Boolean)
    .sort()
    .join('|');
};

const SmartContentPopin = ({
  isOpen,
  onClose,
  onSubmit,
  agents = [],
  agentsLoading = false,
  initialValues = {},
  existingKnowledgeBaseFiles: existingKnowledgeBaseFilesProp,
  mode = 'create',
  llmOptions,
  isTargetUrlRequired = false,
  disableGenerateButton = false,
  onBlockedGenerateClick,
  gapAnalysisEnabled = true,
  showAdvancedSeoSection = true,
  showWritingAxis = true,
  showAgentContentTypeInDropdown = false,
  onNavigateToAgentEdit,
  contentType = '',
  presetOptions: presetOptionsProp,
  lockLlm = false,
  language: languageProp = null,
  country: countryProp = null,
  lockAgent = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const isConsultantRoute = history.location.pathname.startsWith('/Consultant');

  const [form, setForm] = useState({
    keyword: '',
    agentId: '',
    llm: DEFAULT_LLM,
    preset: '',
    presetWordCount: '',
    targetUrl: '',
    existingText: '',
    semanticGap: '',
    internalLinking: '',
    writingAxis: '',
    retranscription: '',
  });
  const [isGapModalOpen, setIsGapModalOpen] = useState(false);
  const [gapMode, setGapMode] = useState('aqisis');
  const [orderWebsite, setOrderWebsite] = useState('');
  const [websiteLanguage, setWebsiteLanguage] = useState(null);
  const [websiteCountry, setWebsiteCountry] = useState(null);
  const [isSeoSectionOpen, setIsSeoSectionOpen] = useState(false);
  const [isKnowledgeBaseSectionOpen, setIsKnowledgeBaseSectionOpen] = useState(false);
  const [isPresetOpen, setIsPresetOpen] = useState(false);
  const [isLlmOpen, setIsLlmOpen] = useState(false);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [canCreateContent, setCanCreateContent] = useState(true);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [isContentLimitModalOpen, setIsContentLimitModalOpen] = useState(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState([]);
  const [existingKnowledgeBaseFiles, setExistingKnowledgeBaseFiles] = useState([]);
  const [trackKeptKnowledgeBaseFileIds, setTrackKeptKnowledgeBaseFileIds] = useState(false);
  const [knowledgeBaseFileError, setKnowledgeBaseFileError] = useState('');
  const lastQuotaFetchKeyRef = useRef('');
  const quotaFetchInFlightRef = useRef(false);
  const wasOpenRef = useRef(false);
  const lastInitialValuesKeyRef = useRef('');
  const appliedExistingKnowledgeBaseKeyRef = useRef(null);

  const resolvedExistingKnowledgeBaseFiles = Array.isArray(existingKnowledgeBaseFilesProp)
    ? existingKnowledgeBaseFilesProp
    : Array.isArray(initialValues?.knowledgeBaseFiles)
      ? initialValues.knowledgeBaseFiles
      : null;
  const resolvedExistingKnowledgeBaseKey = getExistingKnowledgeBaseFilesKey(
    resolvedExistingKnowledgeBaseFiles
  );
  const displayedExistingKnowledgeBaseFiles =
    existingKnowledgeBaseFiles.length > 0
      ? existingKnowledgeBaseFiles
      : normalizeExistingKnowledgeBaseFiles(resolvedExistingKnowledgeBaseFiles || []);

  const isYoutubeMode = String(contentType || '').toUpperCase() === 'YOUTUBE';

  const normalizeUrl = (input) =>
    String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');

  const checkWebsiteQuota = async (websiteValue) => {
    const normalizedWebsite = normalizeUrl(websiteValue);
    if (!normalizedWebsite) return true;

    try {
      const websitesResponse = await axiosPrivate.get('/project/website/all');
      const websites = Array.isArray(websitesResponse?.data) ? websitesResponse.data : [];
      const matchingWebsite = websites.find((websiteItem) => {
        const candidate = normalizeUrl(websiteItem?.urlSite || websiteItem?.url || websiteItem?.name || '');
        return candidate === normalizedWebsite;
      });
      if (matchingWebsite?.language) {
        setWebsiteLanguage(matchingWebsite.language);
      }
      if (matchingWebsite?.country) {
        setWebsiteCountry(matchingWebsite.country);
      }
      if (!matchingWebsite?.id) return true;

      const quotasResponse = await axiosPrivate.get(`/project/website-quotas/${matchingWebsite.id}`);
      const maxTexts = Number(quotasResponse?.data?.maxTexts ?? 0);
      const usedTexts = Number(quotasResponse?.data?.usedTexts ?? 0);
      return maxTexts - usedTexts > 0;
    } catch {
      return true;
    }
  };

  const fetchQuotaStatus = async (websiteValue) => {
    const normalizedWebsite = normalizeUrl(websiteValue);
    const fetchKey = `${isOpen ? 'open' : 'closed'}:${normalizedWebsite}`;

    // Prevent duplicate quota calls for the same website while modal state is unchanged.
    if (lastQuotaFetchKeyRef.current === fetchKey || quotaFetchInFlightRef.current) {
      return;
    }
    lastQuotaFetchKeyRef.current = fetchKey;
    quotaFetchInFlightRef.current = true;

    try {
      const [{ data }, websiteQuotaAllowed] = await Promise.all([
        axiosPrivate.get('/project/company-quotas'),
        checkWebsiteQuota(websiteValue),
      ]);
      setCanCreateContent(websiteQuotaAllowed);
      const totalCredits = Number(data?.totalCredits ?? 0);
      const usedCredits = Number(data?.usedCredits ?? 0);
      if (!Number.isNaN(totalCredits) && !Number.isNaN(usedCredits)) {
        setRemainingCredits(Math.max(totalCredits - usedCredits, 0));
      } else {
        setRemainingCredits(null);
      }
    } catch {
      setCanCreateContent(true);
      setRemainingCredits(null);
    } finally {
      quotaFetchInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      lastInitialValuesKeyRef.current = '';
      lastQuotaFetchKeyRef.current = '';
      setForm({
        keyword: '',
        agentId: '',
        llm: DEFAULT_LLM,
        preset: '',
        presetWordCount: '',
        targetUrl: '',
        existingText: '',
        semanticGap: '',
        internalLinking: '',
        writingAxis: '',
        retranscription: '',
      });
      setIsPresetOpen(false);
      setIsLlmOpen(false);
      setIsSeoSectionOpen(false);
      setIsKnowledgeBaseSectionOpen(false);
      setKnowledgeBaseFiles([]);
      setExistingKnowledgeBaseFiles([]);
      setTrackKeptKnowledgeBaseFileIds(false);
      setKnowledgeBaseFileError('');
      appliedExistingKnowledgeBaseKeyRef.current = null;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (justOpened && String(contentType || '').toUpperCase() === 'YOUTUBE') {
      setIsSeoSectionOpen(false);
    }

    try {
      const raw = window.localStorage.getItem('AC_Dashboard_SelectedWebsite');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const value =
            parsed?.urlSite || parsed?.url || parsed?.name || String(raw);
          setOrderWebsite(value);
          if (parsed?.language) {
            setWebsiteLanguage(parsed.language);
          }
          if (parsed?.country) {
            setWebsiteCountry(parsed.country);
          }
          fetchQuotaStatus(value);
        } catch {
          setOrderWebsite(raw);
          fetchQuotaStatus(raw);
        }
      } else {
        setOrderWebsite('');
        setWebsiteLanguage(null);
        setWebsiteCountry(null);
        fetchQuotaStatus('');
      }
    } catch {
      setOrderWebsite('');
      setWebsiteLanguage(null);
      setWebsiteCountry(null);
      fetchQuotaStatus('');
    }

    if (initialValues) {
      const initialValuesKey = JSON.stringify(initialValues);
      const shouldApplyInitialValues =
        justOpened || lastInitialValuesKeyRef.current !== initialValuesKey;

      if (shouldApplyInitialValues) {
        lastInitialValuesKeyRef.current = initialValuesKey;
        setForm({
          keyword: initialValues.keyword || '',
          agentId: initialValues.agentId || '',
          llm:
            initialValues.llm ||
            (isTargetUrlRequired ? DEFAULT_ENRICHMENT_LLM : DEFAULT_LLM),
          preset: initialValues.preset || '',
          presetWordCount: initialValues.presetWordCount || '',
          targetUrl: initialValues.targetUrl || '',
          existingText: initialValues.existingText ?? initialValues.content ?? '',
          semanticGap: initialValues.semanticGap || '',
          internalLinking: initialValues.internalLinking || '',
          writingAxis: initialValues.writingAxis || '',
          retranscription: initialValues.retranscription || '',
        });
      }
    } else if (justOpened) {
      setForm((prev) => ({
        ...prev,
        llm: isTargetUrlRequired ? DEFAULT_ENRICHMENT_LLM : DEFAULT_LLM,
      }));
    }
  }, [isOpen, initialValues, isTargetUrlRequired, contentType]);

  useEffect(() => {
    if (!isOpen) return;
    if (resolvedExistingKnowledgeBaseFiles === null) return;
    if (appliedExistingKnowledgeBaseKeyRef.current === resolvedExistingKnowledgeBaseKey) {
      return;
    }

    appliedExistingKnowledgeBaseKeyRef.current = resolvedExistingKnowledgeBaseKey;
    const normalizedExisting = normalizeExistingKnowledgeBaseFiles(
      resolvedExistingKnowledgeBaseFiles
    );
    setExistingKnowledgeBaseFiles(normalizedExisting);
    setTrackKeptKnowledgeBaseFileIds(true);
    setKnowledgeBaseFiles([]);
    setKnowledgeBaseFileError('');
    if (normalizedExisting.length > 0) {
      setIsKnowledgeBaseSectionOpen(true);
    }
  }, [
    isOpen,
    resolvedExistingKnowledgeBaseFiles,
    resolvedExistingKnowledgeBaseKey,
  ]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleKnowledgeBaseFileChange = (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    if (input && typeof input.value === 'string') {
      input.value = '';
    }
    if (!files.length) return;

    const validFiles = files.filter(isAcceptedKnowledgeBaseFile);
    if (validFiles.length !== files.length) {
      setKnowledgeBaseFileError(
        t('Format de fichier non supporté. Formats acceptés : PDF, DOCX, TXT, MD, CSV.')
      );
    }

    if (!validFiles.length) return;

    const currentCount =
      displayedExistingKnowledgeBaseFiles.length + knowledgeBaseFiles.length;
    const remaining = MAX_KNOWLEDGE_BASE_FILES - currentCount;

    if (remaining <= 0 || validFiles.length > remaining) {
      setKnowledgeBaseFileError(t('Vous pouvez importer 3 fichiers maximum.'));
    } else if (validFiles.length === files.length) {
      setKnowledgeBaseFileError('');
    }

    if (remaining <= 0) return;

    const acceptedFiles = validFiles.slice(0, remaining);
    setKnowledgeBaseFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const handleKnowledgeBaseFileDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    handleKnowledgeBaseFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleRemoveKnowledgeBaseFile = (indexToRemove) => {
    setKnowledgeBaseFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setKnowledgeBaseFileError('');
  };

  const handleRemoveExistingKnowledgeBaseFile = (indexToRemove) => {
    setExistingKnowledgeBaseFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
    setKnowledgeBaseFileError('');
  };

  const knowledgeBaseFileCount =
    displayedExistingKnowledgeBaseFiles.length + knowledgeBaseFiles.length;
  const isKnowledgeBaseLimitReached = knowledgeBaseFileCount >= MAX_KNOWLEDGE_BASE_FILES;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canCreateContent) {
      onClose?.();
      setIsContentLimitModalOpen(true);
      return;
    }
    if (disableGenerateButton === true) {
      if (typeof onBlockedGenerateClick === 'function') {
        onBlockedGenerateClick();
      }
      return;
    }
    if (onSubmit) {
      let websiteFromStorage = '';
      try {
        const raw = window.localStorage.getItem('AC_Dashboard_SelectedWebsite') || '';
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            websiteFromStorage =
              parsed?.urlSite || parsed?.url || parsed?.name || String(raw);
          } catch {
            websiteFromStorage = raw;
          }
        }
      } catch {
        websiteFromStorage = '';
      }

      const trimmedExistingText = (form.existingText || '').trim();
      const effectiveLlm = lockLlm
        ? (initialValues.llm || form.llm || DEFAULT_LLM)
        : isYoutubeMode
        ? (form.llm || (Array.isArray(llmOptions) && llmOptions[0]?.id) || DEFAULT_LLM)
        : (form.llm || (isTargetUrlRequired ? DEFAULT_ENRICHMENT_LLM : DEFAULT_LLM));
      const effectiveAgentId = lockAgent
        ? (initialValues.agentId || form.agentId)
        : form.agentId;
      const selectedAgentEntry = (agents || []).find(
        (agent) => String(agent?.id || agent?.agentId || '') === String(effectiveAgentId || '')
      );
      const agentLangue = (() => {
        if (!selectedAgentEntry) return '';
        const langue = selectedAgentEntry.langue ?? selectedAgentEntry.language;
        if (langue == null) return '';
        if (typeof langue === 'string' || typeof langue === 'number') {
          return String(langue).trim();
        }
        if (typeof langue === 'object') {
          return String(
            langue.language || langue.name || langue.code || langue.langue || langue.id || ''
          ).trim();
        }
        return '';
      })();
      const payload = {
        ...form,
        agentId: effectiveAgentId,
        llm: effectiveLlm,
        content: trimmedExistingText || undefined,
        website: websiteFromStorage,
        agentLangue,
        knowledgeBaseFiles,
        files: knowledgeBaseFiles,
        ...(trackKeptKnowledgeBaseFileIds ||
        displayedExistingKnowledgeBaseFiles.length > 0 ||
        Array.isArray(resolvedExistingKnowledgeBaseFiles)
          ? {
              keptKnowledgeBaseFileIds: (
                trackKeptKnowledgeBaseFileIds
                  ? existingKnowledgeBaseFiles
                  : displayedExistingKnowledgeBaseFiles
              )
                .map((file) => file.id)
                .filter(Boolean),
            }
          : {}),
        ...(mode === 'create' ? { generateTitleAndMeta: true } : {}),
      };
      if (showWritingAxis) {
        payload.axeRedaction = form.writingAxis || '';
      }
      onSubmit(payload);
    }
  };

  const renderKnowledgeBaseSection = (sectionTitle) => (
    <div className="smart-content-popin-section">
      <h6
        className="smart-content-popin-section-title-seo"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setIsKnowledgeBaseSectionOpen((prev) => !prev)}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/Images/conai-icon.png" alt="info" className="smart-content-popin-info-icon" />
          {sectionTitle}
        </span>
        <span
          style={{
            display: 'inline-block',
            transition: 'transform 0.2s ease',
            transform: isKnowledgeBaseSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            fontSize: 14,
            color: '#818181',
          }}
        >
          ▼
        </span>
      </h6>

      {isKnowledgeBaseSectionOpen && (
        <>
          <p className="smart-content-popin-section-description">
            {t("Optionnel : vos ressources internes aident l’agent à produire des contenus plus précis et personnalisés.")}
          </p>
          <div
            className={`smart-content-popin-upload-dropzone${isKnowledgeBaseLimitReached ? ' is-limit-reached' : ''}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleKnowledgeBaseFileDrop}
          >
            <input
              type="file"
              id="smart-content-kb-upload-input"
              multiple
              accept={KNOWLEDGE_BASE_ACCEPT}
              disabled={isKnowledgeBaseLimitReached}
              style={{ display: 'none' }}
              onChange={handleKnowledgeBaseFileChange}
            />
            <div className="smart-content-popin-upload-dropzone-inner">
              <div className="smart-content-popin-upload-dropzone-icon">
                <img src="/Images/imp-icon.png" alt="Upload" />
              </div>
              <div
                className={`smart-content-popin-upload-dropzone-text-main${isKnowledgeBaseLimitReached ? ' is-limit-message' : ''}`}
              >
                {isKnowledgeBaseLimitReached
                  ? t('Vous avez atteint le nombre maximum de fichiers autorisés pour cet agent.')
                  : t('Glissez-déposez votre fichier')}
              </div>
              {!isKnowledgeBaseLimitReached && (
                <div className="smart-content-popin-upload-dropzone-text-sub">
                  {t('ou cliquez pour parcourir')}
                  {' — '}
                  {t('Formats acceptés : PDF, DOCX, TXT, MD, CSV.')}
                </div>
              )}
              <label
                htmlFor={isKnowledgeBaseLimitReached ? undefined : 'smart-content-kb-upload-input'}
                className={`smart-content-popin-upload-dropzone-button${isKnowledgeBaseLimitReached ? ' is-disabled' : ''}`}
              >
                {t('Parcourir les fichiers')}
              </label>
              {(displayedExistingKnowledgeBaseFiles.length > 0 || knowledgeBaseFiles.length > 0) && (
                <div className="smart-content-popin-upload-dropzone-filelist">
                  {displayedExistingKnowledgeBaseFiles.map((file, idx) => (
                    <div
                      key={`existing-${file.id || file.originalName}-${idx}`}
                      className="smart-content-popin-upload-dropzone-filename-row"
                    >
                      <span
                        className="smart-content-popin-upload-dropzone-filename"
                        title={file.originalName}
                      >
                        {file.originalName || file.id}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (existingKnowledgeBaseFiles.length > 0) {
                            handleRemoveExistingKnowledgeBaseFile(idx);
                          } else {
                            const next = displayedExistingKnowledgeBaseFiles.filter(
                              (_, index) => index !== idx
                            );
                            setExistingKnowledgeBaseFiles(next);
                            setTrackKeptKnowledgeBaseFileIds(true);
                            appliedExistingKnowledgeBaseKeyRef.current =
                              getExistingKnowledgeBaseFilesKey(next);
                            setKnowledgeBaseFileError('');
                          }
                        }}
                        className="smart-content-popin-upload-dropzone-remove"
                        aria-label={t('Supprimer le fichier')}
                      >
                        <img
                          src="/Images/close-iconnn.png"
                          alt={t('Supprimer')}
                          style={{ width: 14, height: 14 }}
                        />
                      </button>
                    </div>
                  ))}
                  {knowledgeBaseFiles.map((file, idx) => (
                    <div
                      key={`new-${file.name}-${idx}`}
                      className="smart-content-popin-upload-dropzone-filename-row"
                    >
                      <span
                        className="smart-content-popin-upload-dropzone-filename"
                        title={file.name}
                      >
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveKnowledgeBaseFile(idx);
                        }}
                        className="smart-content-popin-upload-dropzone-remove"
                        aria-label={t('Supprimer le fichier')}
                      >
                        <img
                          src="/Images/close-iconnn.png"
                          alt={t('Supprimer')}
                          style={{ width: 14, height: 14 }}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {knowledgeBaseFileError ? (
            <div className="smart-content-popin-upload-dropzone-error">{knowledgeBaseFileError}</div>
          ) : null}
        </>
      )}
    </div>
  );

  const gapLanguage = languageProp || websiteLanguage || null;
  const gapCountry = countryProp || websiteCountry || null;

  const defaultLlmOptions = [
    { id: 'claude_gpt', name: 'Claude + GPT' },
    { id: 'gpt', name: 'GPT' },
    { id: 'claude', name: 'Claude' },
    { id: 'gemini', name: 'Gemini' },
    { id: 'perplexity', name: 'Perplexity' },
  ];
  const enrichmentLlmOptions = [
    { id: 'claude', name: 'Claude' },
    { id: 'gemini', name: 'Gemini' },
  ];
  const selectableLlmOptions = isTargetUrlRequired
    ? enrichmentLlmOptions
    : Array.isArray(llmOptions) && llmOptions.length > 0
      ? llmOptions
      : defaultLlmOptions;
  const presetOptions = presetOptionsProp || [
    { id: 'ARTICLE_COURT_300', name: t('Article court - 300 mots') },
    { id: 'ARTICLE_MOYEN_600', name: t('Article moyen - 600 mots') },
    { id: 'ARTICLE_LONG_1000', name: t('Article long - 1000 mots') },
    { id: 'YOUTUBE_350', name: t('Youtube - 350 mots') },
    { id: 'LINKEDIN_200', name: t('Linkedin - 200 mots') },
    { id: 'X_150', name: t('X - 150 mots') },
    { id: 'CUSTOM', name: t('Personnalisé'), isCustom: true },
  ];
  const hasTargetUrl = !!(form.targetUrl || '').trim();
  const isFormReady = isYoutubeMode
    ? !!form.keyword && !!form.agentId && !!form.preset
    : !!form.keyword &&
      !!form.agentId &&
      !!form.llm &&
      !!form.preset &&
      (!isTargetUrlRequired || hasTargetUrl);

  const formatAgentContentType = (contentType) => {
    const key = String(contentType || '').toUpperCase();
    switch (key) {
      case 'LONG_CATEGORY':
        return t('Page catégorie');
      case 'PRODUCT':
        return t('Fiche produit');
      case 'STORE_LOCATOR':
        return t('Store locator');
      case 'GMB':
        return t('GBP');
      case 'OTHER':
        return t('Contenu libre');
      case 'CONTENT_IMPROVEMENT':
        return t('Enrichissement');
      default:
        return contentType || '';
    }
  };

  const getAgentName = (agent) => agent?.name || agent?.agentName || t('Agent IA');

  const getAgentContentTypeLabel = (agent) =>
    formatAgentContentType(agent?.typeContenu || agent?.contentType);

  const renderAgentOptionLabel = (agent) => {
    const agentName = getAgentName(agent);
    const contentTypeLabel = showAgentContentTypeInDropdown
      ? getAgentContentTypeLabel(agent)
      : '';

    if (!contentTypeLabel) {
      return agentName;
    }

    return (
      <span className="smart-content-popin-agent-option">
        <span className="smart-content-popin-agent-option-name">{agentName}</span>
        <span className="smart-content-popin-agent-option-type">{contentTypeLabel}</span>
      </span>
    );
  };

 
  const renderLlmOptionLabel = (option, isSelected = false) => {
    if (isSelected) {
      return option?.name || '';
    }

    return (
      <span className="smart-content-popin-llm-option">
        <span
          className={
            option?.disabled
              ? 'smart-content-popin-llm-option-name is-disabled'
              : 'smart-content-popin-llm-option-name'
          }
        >
          {option?.name}
        </span>
        {option?.disabled ? (
          <span className="smart-content-popin-soon-badge">{t('Bientôt disponible')}</span>
        ) : null}
      </span>
    );
  };

  const dropdownInputStyle = { backgroundColor: '#ffffff' };
  const isInlineDropdownOpen = (isPresetOpen || isLlmOpen) && !isSeoSectionOpen;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      className={`smart-content-popin${isInlineDropdownOpen ? ' preset-dropdown-open' : ''}`}
    >
      <ModalHeader className="modal-title" toggle={onClose}>
        {mode === 'edit'
          ? t('Paramètres du contenu généré')
          : t('Créer un nouveau contenu')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {isYoutubeMode ? (
            <>
              <div className="smart-content-popin-section">
                <h6 className="smart-content-popin-section-title">
                  <img src="/Images/info-glob.png" alt="info" className="smart-content-popin-info-icon" />
                  {t('Section 1 – Informations requises (obligatoire)')}
                </h6>
                <FormGroup>
                  <Label for="smartKeyword" className="smart-content-popin-label">
                    {t('Mot-clé')}
                    {' '}
                    <span className="smart-content-popin-required">*</span>
                  </Label>
                  <Input
                    id="smartKeyword"
                    type="text"
                    placeholder={t('Ex: télépéage automatique')}
                    value={form.keyword}
                    onChange={handleChange('keyword')}
                    className="smart-content-popin-input keyword-input"
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="smartAgent" className="smart-content-popin-label">
                    {t('Agent IA')}
                    {' '}
                    <span className="smart-content-popin-required">*</span>
                  </Label>
                  <CustomSelectDropdown
                    options={agentsLoading ? [] : (agents || [])}
                    value={
                      agentsLoading
                        ? null
                        : (agents || []).find((a) => String(a.id || a.agentId) === String(form.agentId)) || null
                    }
                    onChange={(selected) => {
                      setForm((prev) => ({
                        ...prev,
                        agentId: selected ? (selected.id || selected.agentId || '') : '',
                      }));
                    }}
                    getOptionLabel={getAgentName}
                    renderOptionLabel={renderAgentOptionLabel}
                    getOptionValue={(agent) => agent?.id || agent?.agentId}
                    placeholder={
                      agentsLoading
                        ? t('Recherche des agents...')
                        : (agents || []).length === 0
                          ? t('Aucun agent disponible avec ce type de contenu')
                          : t('Sélectionnez un agent IA')
                    }
                    disabled={agentsLoading || (agents || []).length === 0}
                    hideDropdownIcon={!agentsLoading && (agents || []).length === 0}
                    className="smart-content-popin-input smart-content-popin-agent-dropdown"
                    inputStyle={dropdownInputStyle}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="smartPreset" className="smart-content-popin-label">
                    {t('Preset de mots / prompts')}
                    {' '}
                    <span className="smart-content-popin-required">*</span>
                  </Label>
                  <CustomSelectDropdown
                    options={presetOptions}
                    value={presetOptions.find((p) => p.id === form.preset) || null}
                    onMenuOpen={() => setIsPresetOpen(true)}
                    onMenuClose={() => setIsPresetOpen(false)}
                    onChange={(selected) => {
                      setForm((prev) => ({
                        ...prev,
                        preset: selected ? selected.id : '',
                        presetWordCount: selected && selected.isCustom ? prev.presetWordCount : '',
                      }));
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    getOptionValue={(option) => option?.id}
                    menuInline={!isSeoSectionOpen}
                    placeholder={t('Sélectionnez un format')}
                    className="smart-content-popin-input"
                    inputStyle={dropdownInputStyle}
                  />
                </FormGroup>

                {presetOptions.find((p) => p.id === form.preset)?.isCustom && (
                  <FormGroup>
                    <Label className="smart-content-popin-label">
                      {t('Nombre de mots par texte')}
                    </Label>
                    <div className="smart-content-popin-custom-words-wrapper">
                      <Input
                        type="number"
                        min="1"
                        value={form.presetWordCount}
                        onChange={handleChange('presetWordCount')}
                        placeholder="350"
                        className="smart-content-popin-input smart-content-popin-custom-words-input"
                      />
                      <span className="smart-content-popin-word-suffix">
                        {t('mots')}
                      </span>
                    </div>
                  </FormGroup>
                )}
              </div>

              <div className="smart-content-popin-section">
                <h6
                  className="smart-content-popin-section-title-seo"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsSeoSectionOpen((prev) => !prev)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src="/Images/seo-settings.png" alt="info" className="smart-content-popin-info-icon" />
                    {t('Section 2 – Paramètres SEO avancés (optionnel)')}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s ease',
                      transform: isSeoSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      fontSize: 14,
                      color: '#818181',
                    }}
                  >
                    ▼
                  </span>
                </h6>

                {isSeoSectionOpen && (
                  <>
                    <FormGroup>
                      <Label for="smartTargetUrlYoutube" className="smart-content-popin-label">
                        {t('URL')}
                      </Label>
                      <Input
                        id="smartTargetUrlYoutube"
                        type="url"
                        placeholder={t('Ex: https://www.youtube.com/watch?v=...')}
                        value={form.targetUrl}
                        onChange={handleChange('targetUrl')}
                        className="smart-content-popin-input smart-content-popin-url-input"
                      />
                    </FormGroup>

                    {hasTargetUrl && (
                      <FormGroup>
                        <Label for="smartExistingTextYoutube" className="smart-content-popin-label">
                          {t('Description existante')}
                        </Label>
                        <Input
                          id="smartExistingTextYoutube"
                          type="textarea"
                          rows="5"
                          placeholder={t('Collez la description existante de la vidéo.')}
                          value={form.existingText}
                          onChange={handleChange('existingText')}
                          className="smart-content-popin-input smart-content-popin-existing-text-input semantic-gap-textarea"
                        />
                      </FormGroup>
                    )}

                    <FormGroup>
                      <Label for="smartRetranscription" className="smart-content-popin-label">
                        {t('Retranscription')}
                      </Label>
                      <Input
                        id="smartRetranscription"
                        type="textarea"
                        rows="5"
                        placeholder={t('Collez la retranscription de la vidéo.')}
                        value={form.retranscription}
                        onChange={handleChange('retranscription')}
                        className="smart-content-popin-input semantic-gap-textarea"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label for="smartSemanticGapYoutube" className="smart-content-popin-label">
                        {t('Gap sémantique')}
                      </Label>
                      <Input
                        id="smartSemanticGapYoutube"
                        type="textarea"
                        rows="4"
                        placeholder={t('keyword1 (2),\nkeyword2 (1),\nkeyword3 (1),\nkeyword4 (2),\n.....')}
                        value={form.semanticGap}
                        onChange={handleChange('semanticGap')}
                        className="semantic-gap-textarea smart-content-popin-input smart-content-popin-semantic-gap-input"
                      />
                      <div className="semantic-gap-actions smart-content-popin-gap-actions">
                        <button
                          type="button"
                          className="gap-light-btn"
                          onClick={() => {
                            if (gapAnalysisEnabled !== true) return;
                            setIsGapModalOpen(true);
                          }}
                          disabled={gapAnalysisEnabled !== true}
                        >
                          <span className="gap-refresh-icon">
                            <img
                              src="/Images/gap-icon-gene.png"
                              alt={t('Générer une couverture sémantique')}
                              style={{ width: 16, height: 16, marginLeft: -6 }}
                            />
                          </span>
                          <span className="gap-refresh-label">
                            {t('Générer une couverture sémantique')}
                          </span>
                        </button>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label for="smartInternalLinkingYoutube" className="smart-content-popin-label">
                        {t('Maillage interne')}
                      </Label>
                      <Input
                        id="smartInternalLinkingYoutube"
                        type="textarea"
                        rows="3"
                        placeholder={t('URLs des pages à lier (séparées par des virgules)')}
                        value={form.internalLinking}
                        onChange={handleChange('internalLinking')}
                        className="smart-content-popin-input smart-content-popin-internal-linking-input"
                      />
                    </FormGroup>
                  </>
                )}
              </div>

              {renderKnowledgeBaseSection(t('Section 3 – Base de connaissance (optionnel)'))}
            </>
          ) : (
            <>
          {/* Section 1 — Informations requises */}
          <div className="smart-content-popin-section">
            <h6 className="smart-content-popin-section-title">
              <img src="/Images/info-glob.png" alt="info" className="smart-content-popin-info-icon" />
              {t('Section 1 — Informations requises')}
            </h6>
            <FormGroup>
              <Label for="smartKeyword" className="smart-content-popin-label">
                {t('Mot-clé principal')}{' '}
                <span className="smart-content-popin-required">*</span>
              </Label>
              <Input
                id="smartKeyword"
                type="text"
                placeholder={t('Ex: télépéage automatique')}
                value={form.keyword}
                onChange={handleChange('keyword')}
                className="smart-content-popin-input keyword-input"
              />
            </FormGroup>

            {isTargetUrlRequired && (
              <FormGroup>
                <Label for="smartTargetUrl" className="smart-content-popin-label">
                  {t('URL cible')}
                  {' '}
                  <span className="smart-content-popin-required">*</span>
                </Label>
                <Input
                  id="smartTargetUrl"
                  type="url"
                  placeholder={t('Ex: https://www.exemple.com/page-cible')}
                  value={form.targetUrl}
                  onChange={handleChange('targetUrl')}
                  required
                  className="smart-content-popin-input smart-content-popin-url-input"
                />
              </FormGroup>
            )}

            {isTargetUrlRequired && (
              <FormGroup>
                <Label for="smartExistingText" className="smart-content-popin-label">
                  {t('Texte existant (optionnel)')}
                </Label>
                <Input
                  id="smartExistingText"
                  type="textarea"
                  rows="5"
                  placeholder={t('Collez le texte existant associé à cette URL.')}
                  value={form.existingText}
                  onChange={handleChange('existingText')}
                  className="smart-content-popin-input smart-content-popin-existing-text-input semantic-gap-textarea"
                />
              </FormGroup>
            )}
          </div>

          {/* Section 2 — Génération IA */}
          <div className="smart-content-popin-section">
            <h6 className="smart-content-popin-section-title">
              <img src="/Images/ai-popin-icon.png" alt="info" className="smart-content-popin-info-icon" />
              {t('Section 2 — Génération IA')}
            </h6>

            <FormGroup>
              <Label for="smartAgent" className="smart-content-popin-label">
                {t('Agent IA')}
              </Label>
              {lockAgent ? (
                <Input
                  id="smartAgent"
                  type="text"
                  value={(() => {
                    const selectedAgent = (agents || []).find(
                      (a) => String(a.id || a.agentId) === String(form.agentId)
                    );
                    if (!selectedAgent) return form.agentId;
                    const typeLabel = showAgentContentTypeInDropdown
                      ? getAgentContentTypeLabel(selectedAgent)
                      : '';
                    const agentName = getAgentName(selectedAgent);
                    return typeLabel ? `${agentName} — ${typeLabel}` : agentName;
                  })()}
                  readOnly
                  disabled
                  className="smart-content-popin-input"
                />
              ) : (
                <CustomSelectDropdown
                  options={agentsLoading ? [] : (agents || [])}
                  value={
                    agentsLoading
                      ? null
                      : (agents || []).find((a) => String(a.id || a.agentId) === String(form.agentId)) || null
                  }
                  onChange={(selected) => {
                    setForm((prev) => ({
                      ...prev,
                      agentId: selected ? (selected.id || selected.agentId || '') : '',
                    }));
                  }}
                  getOptionLabel={getAgentName}
                  renderOptionLabel={renderAgentOptionLabel}
                  getOptionValue={(agent) => agent?.id || agent?.agentId}
                  placeholder={
                    agentsLoading
                      ? t('Recherche des agents...')
                      : (agents || []).length === 0
                        ? t('Aucun agent disponible avec ce type de contenu')
                        : t('Sélectionnez un agent IA')
                  }
                  disabled={agentsLoading || (agents || []).length === 0}
                  hideDropdownIcon={!agentsLoading && (agents || []).length === 0}
                  className="smart-content-popin-input smart-content-popin-agent-dropdown"
                  inputStyle={dropdownInputStyle}
                  renderOptionEnd={
                    mode === 'edit' && typeof onNavigateToAgentEdit === 'function'
                      ? (agent) => (
                        <img
                          src="Images/icon-edit.png"
                          alt={t('Modifier cet agent')}
                          title={t('Modifier cet agent')}
                          style={{ width: 16, height: 16, cursor: 'pointer' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const agentId = agent?.id || agent?.agentId;
                            if (agentId) onNavigateToAgentEdit(agentId);
                          }}
                        />
                      )
                      : null
                  }
                />
              )}
            </FormGroup>

            <FormGroup>
              <Label for="smartLlm" className="smart-content-popin-label">
                {t('LLM')}
              </Label>
              {lockLlm ? (
                <Input
                  id="smartLlm"
                  type="text"
                  value={
                    selectableLlmOptions.find((m) => m.id === form.llm)?.name ||
                    form.llm ||
                    ''
                  }
                  readOnly
                  disabled
                  className="smart-content-popin-input"
                />
              ) : (
                <CustomSelectDropdown
                  options={selectableLlmOptions}
                  value={selectableLlmOptions.find((m) => m.id === form.llm) || null}
                  onMenuOpen={() => setIsLlmOpen(true)}
                  onMenuClose={() => setIsLlmOpen(false)}
                  onChange={(selected) => {
                    if (selected?.disabled) return;
                    setForm((prev) => ({
                      ...prev,
                      llm: selected ? selected.id : '',
                    }));
                  }}
                  getOptionLabel={(option) => option?.name || ''}
                  getOptionValue={(option) => option?.id}
                  renderOptionLabel={renderLlmOptionLabel}
                  isOptionDisabled={(option) => !!option?.disabled}
                  menuInline={!isSeoSectionOpen}
                  placeholder={t('Sélectionnez un modèle')}
                  className="smart-content-popin-input"
                  inputStyle={dropdownInputStyle}
                />
              )}
            </FormGroup>

            <FormGroup>
              <Label for="smartPreset" className="smart-content-popin-label">
                {t('Preset de mots')}
              </Label>
              <CustomSelectDropdown
                options={presetOptions}
                value={presetOptions.find((p) => p.id === form.preset) || null}
                onMenuOpen={() => setIsPresetOpen(true)}
                onMenuClose={() => setIsPresetOpen(false)}
                onChange={(selected) => {
                  setForm((prev) => ({
                    ...prev,
                    preset: selected ? selected.id : '',
                    presetWordCount: selected && selected.isCustom ? prev.presetWordCount : '',
                  }));
                }}
                getOptionLabel={(option) => option?.name || ''}
                getOptionValue={(option) => option?.id}
                menuInline={!isSeoSectionOpen}
                placeholder={t('Sélectionnez un format')}
                className="smart-content-popin-input"
                inputStyle={dropdownInputStyle}
              />
            </FormGroup>

            {presetOptions.find((p) => p.id === form.preset)?.isCustom && (
              <FormGroup>
                <Label className="smart-content-popin-label">
                  {t('Nombre de mots par texte')}
                </Label>
                <div className="smart-content-popin-custom-words-wrapper">
                  <Input
                    type="number"
                    min="1"
                    value={form.presetWordCount}
                    onChange={handleChange('presetWordCount')}
                    placeholder="850"
                    className="smart-content-popin-input smart-content-popin-custom-words-input"
                  />
                  <span className="smart-content-popin-word-suffix">
                    {t('mots')}
                  </span>
                </div>
              </FormGroup>
            )}
          </div>

          {showAdvancedSeoSection && (
            <>
              {/* Section 3 — Paramètres SEO (repliable) */}
              <div className="smart-content-popin-section">
                <h6
                  className="smart-content-popin-section-title-seo"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsSeoSectionOpen((prev) => !prev)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src="/Images/seo-settings.png" alt="info" className="smart-content-popin-info-icon" />
                    {t('Section 3 — Paramètres SEO avancés (optionnel)')}
                  </span>
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s ease',
                      transform: isSeoSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                      fontSize: 14,
                      color: '#818181',
                    }}
                  >
                    ▼
                  </span>
                </h6>

                {isSeoSectionOpen && (
                  <>
                    {!isTargetUrlRequired && (
                      <FormGroup>
                        <Label for="smartTargetUrlSeo" className="smart-content-popin-label">
                          {t('URL cible')}
                        </Label>
                        <Input
                          id="smartTargetUrlSeo"
                          type="url"
                          placeholder={t('Ex: https://www.exemple.com/page-cible')}
                          value={form.targetUrl}
                          onChange={handleChange('targetUrl')}
                          className="smart-content-popin-input smart-content-popin-url-input"
                        />
                      </FormGroup>
                    )}

                    <FormGroup>
                      <Label for="smartSemanticGap" className="smart-content-popin-label">
                        {t('Gap sémantique')}
                        <span
                          id="gapTooltipSmart"
                          className="ms-2"
                          style={{ cursor: 'pointer', color: '#ef4444' }}
                        >
                          ⓘ
                        </span>
                      </Label>
                      <Input
                        id="smartSemanticGap"
                        type="textarea"
                        rows="4"
                        placeholder={t('keyword1 (2),\nkeyword2 (1),\nkeyword3 (1),\nkeyword4 (2),\n.....')}
                        value={form.semanticGap}
                        onChange={handleChange('semanticGap')}
                        className="semantic-gap-textarea smart-content-popin-input smart-content-popin-semantic-gap-input"
                      />
                      <div className="semantic-gap-actions smart-content-popin-gap-actions">
                        <button
                          type="button"
                          className="gap-light-btn"
                          onClick={() => {
                            if (gapAnalysisEnabled !== true) return;
                            setIsGapModalOpen(true);
                          }}
                          disabled={gapAnalysisEnabled !== true}
                        >
                          <span className="gap-refresh-icon">
                            <img
                              src="/Images/gap-icon-gene.png"
                              alt={t('Générer une couverture sémantique')}
                              style={{ width: 16, height: 16, marginLeft: -6 }}
                            />
                          </span>
                          <span className="gap-refresh-label">
                            {t('Générer une couverture sémantique')}
                          </span>
                        </button>
                      </div>
                    </FormGroup>

                    <FormGroup>
                      <Label for="smartInternalLinking" className="smart-content-popin-label">
                        {t('Maillage interne')}
                      </Label>
                      <Input
                        id="smartInternalLinking"
                        type="textarea"
                        rows="3"
                        placeholder={t('URLs des pages à lier (séparées par des virgules)')}
                        value={form.internalLinking}
                        onChange={handleChange('internalLinking')}
                        className="smart-content-popin-input smart-content-popin-internal-linking-input"
                      />
                    </FormGroup>

                    {showWritingAxis && (
                      <FormGroup>
                        <Label for="smartWritingAxis" className="smart-content-popin-label">
                          {t('Axe de rédaction')}
                        </Label>
                        <Input
                          id="smartWritingAxis"
                          type="textarea"
                          rows="3"
                          placeholder={t('Indiquez un angle éditorial (optionnel)')}
                          value={form.writingAxis}
                          onChange={handleChange('writingAxis')}
                          className="smart-content-popin-input"
                        />
                      </FormGroup>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {renderKnowledgeBaseSection(t('Section 4 — Base de connaissance (optionnel)'))}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" onClick={onClose} className="smart-content-popin-cancel"
          >
            {t('Annuler')}
          </Button>
          <Button
            type={disableGenerateButton === true && isFormReady ? 'button' : 'submit'}
            className="smart-content-popin-button"
            onClick={
              disableGenerateButton === true && isFormReady && typeof onBlockedGenerateClick === 'function'
                ? onBlockedGenerateClick
                : undefined
            }
            disabled={!isFormReady}
          >
            {t('Générer le contenu')}
          </Button>
        </ModalFooter>
      </Form>

      <GapGenerationModal
        isOpen={isGapModalOpen}
        onClose={() => setIsGapModalOpen(false)}
        mode={gapMode}
        onModeChange={setGapMode}
        keyword={form.keyword}
        orderWebsite={orderWebsite}
        url={form.targetUrl}
        language={gapLanguage}
        country={gapCountry}
        contentScore={null}
        handleKeywordChange={({ target: { value } }) =>
          handleChange('semanticGap')({ target: { value } })
        }
        onUpdate={() => { }}
        onLightGapError={() => { }}
        disableLightGap={gapAnalysisEnabled !== true}
      />
      <ProjectCreatedModal
        isOpen={isContentLimitModalOpen}
        onClose={() => setIsContentLimitModalOpen(false)}
        title={t('Limite de contenus atteinte')}
        message={t("Vous avez atteint le nombre maximum de contenus inclus dans votre forfait. Passez à une offre supérieure pour continuer à créer et optimiser vos contenus.")}
        confirmButtonText={t('Passer au forfait supérieur')}
        onConfirm={() => {
          setIsContentLimitModalOpen(false);
          if (!isConsultantRoute) {
            history.push('/AdminClient/Abonnement');
          }
        }}
        secondaryButtonText={t('Fermer')}
        onSecondaryAction={() => {
          setIsContentLimitModalOpen(false);
        }}
      />
      <ProjectCreatedModal
        isOpen={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
        title={t('Limite atteinte')}
        message={t('Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.')}
        confirmButtonText={t('Acheter des crédits')}
        onConfirm={() => {
          setIsCreditsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => {
          setIsCreditsLimitModalOpen(false);
          if (!isConsultantRoute) {
            history.push('/AdminClient/Abonnement');
          }
        }}
      />
      <BuyCreditsModal open={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />
    </Modal>
  );
};

export default SmartContentPopin;